import * as React from "react";
import { DatasheetElementSettingsFieldComponentValue, DatasheetElementSettingsTextComponentValue } from "../../components/datasheet-element-settings/controllers/main";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";

type Props = {
  normalizeDatasheetElementsTabOrder: (elements: Record<DatasheetElementId, DatasheetElement>) => Record<DatasheetElementId, DatasheetElement>;
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  getDatasheetElement: (id: DatasheetElementId) => DatasheetElement | undefined;
  selectedDatasheetElementsId: Array<DatasheetElementId>;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
};

export const useDatasheetElementSettingsController = (props: Props) => {
  const { 
    selectedDatasheetElementsId, 
    datasheetElements,
    getDatasheetElement, 
    onDatasheetElementsChange, 
    normalizeDatasheetElementsTabOrder 
  } = props;

  const mapTextComponentSettingsToElement = React.useCallback((value: DatasheetElementSettingsTextComponentValue): Partial<DatasheetTextComponentGetDto> => {
    return {
      textstyle: value.textStyle,
      fontColor: value.color ? parseInt(value.color.replace('#', ''), 16) : undefined,
      fontsize: value.fontSize,
      fontname: value.fontFamily,
      overStrike: false,
      dashes: false,
      verticalLine: false,
      text: value.text,
    };
  }, []);

  const mapFieldComponentSettingsToElement = React.useCallback((value: DatasheetElementSettingsFieldComponentValue): Partial<DatasheetFieldComponentGetDto> => {
    return {
      fontname: value.content.fontFamily,
      fontsize: value.content.fontSize,
      fontcolor: parseInt(value.content.color.replace('#', ''), 16),
      showfieldtype: value.content.showFieldType,
      textstyle: value.content.styles,
      contentalign: value.content.align,
      taborder: value.content.tabOrder,
      showborder: value.content.showBorder,

      headfont: value.heading.fontFamily,
      headfontsize: value.heading.fontSize,
      headcolor: parseInt(value.heading.color.replace('#', ''), 16),
      headpos: value.heading.position,
      headalign: value.heading.align,
      headstyle: value.heading.textStyles,
      headtext: value.heading.textValue,
      headdisplay: value.heading.displayHeading,

      helptext: value.helpText.textValue,
    };
  }, []);

  const mapElementToFieldComponentSettings = React.useCallback((element: DatasheetElement<DatasheetFieldComponentGetDto>): DatasheetElementSettingsFieldComponentValue => {
    return {
      content: {
        showBorder: element.original.showborder,
        fontFamily: element.original.fontname,
        fontSize: element.original.fontsize,
        color: `#${element.original.fontcolor.toString(16)}`,
        showFieldType: element.original.showfieldtype,
        styles: element.original.textstyle,
        align: element.original.contentalign,
        tabOrder: element.original.taborder,
      },
      heading: {
        displayHeading: element.original.headdisplay,
        textValue: element.original.headtext,
        fontFamily: element.original.headfont,
        fontSize: element.original.headfontsize,
        color: `#${element.original.headcolor.toString(16)}`,
        position: element.original.headpos,
        align: element.original.headalign,
        textStyles: element.original.headstyle,
      },
      helpText: {
        textValue: element.original.helptext,
      },
    };
  }, []);

  const handleTextComponentSettingsChange = React.useCallback((value: DatasheetElementSettingsTextComponentValue) => {
    //
  }, []);

  const handleFieldComponentSettingsChange = React.useCallback((value: DatasheetElementSettingsFieldComponentValue) => {
    let tabOrderShouldBeUpdated = false;

    const updatedElements = selectedDatasheetElementsId.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, elementId) => {
      const element = getDatasheetElement(elementId) as DatasheetElement<DatasheetFieldComponentGetDto> | undefined;

      if (element?.type !== DatasheetElementType.field) {
        return acc;
      }

      if (element.original.taborder !== value.content.tabOrder) {
        tabOrderShouldBeUpdated = true;
      }

      const updatedElement: DatasheetElement = {
        ...element,
        original: {
          ...element.original,
          ...mapFieldComponentSettingsToElement(value),
        }
      };

      acc[element.id] = updatedElement;

      return acc;
    }, {});

    const mergedElements = {
      ...datasheetElements,
      ...updatedElements, 
    };

    if (tabOrderShouldBeUpdated) {
      onDatasheetElementsChange(normalizeDatasheetElementsTabOrder(mergedElements));
      return;
    }

    onDatasheetElementsChange(mergedElements);
  }, [selectedDatasheetElementsId, onDatasheetElementsChange, getDatasheetElement, normalizeDatasheetElementsTabOrder]);

  const currentFieldComponentMetadata = React.useMemo(() => {
    const firstSelectedId = selectedDatasheetElementsId[0];
    
    if (firstSelectedId) {
      const element = getDatasheetElement(firstSelectedId);

      if (element?.type === DatasheetElementType.field) {
        return {
          fieldComponentValue: mapElementToFieldComponentSettings(element as DatasheetElement<DatasheetFieldComponentGetDto>),
          datasheetElement: element,
        }
      }
    }
  }, [selectedDatasheetElementsId, getDatasheetElement]);

  return {
    currentFieldComponentMetadata,
    mapFieldComponentSettingsToElement,
    mapElementToFieldComponentSettings,
    handleFieldComponentSettingsChange,
    handleTextComponentSettingsChange,
    mapTextComponentSettingsToElement
  };
};